import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Button, Icon, InfiniteScrollingObserver, SingleSelect, Tooltip } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { colors, weights } from '~/styles';
import { dateFormats, emptyStringToNull } from '~/utils';
import Actions from './Actions';
import Members from './Members';
import General from './General';
import { useImpersonation } from '../contexts';

const SearchArea = styled.div`
  display: flex;
  flex-direction: column;
`;

const SearchFilters = styled.div`
  display: flex;
`;

const SearchInput = styled.div`
  flex: 1;
  position: relative;
`;

const ClearButton = styled(Button)`
  position: absolute;
  top: 50%;
  right: 0.875rem;
  transform: translateY(-50%);
`;

const SearchIndicator = styled.div`
  position: absolute;
  top: 50%;
  right: ${({ offset }) => offset}rem;
  transform: translateY(-50%);
  color: ${colors.primary};
`;

const StatusInput = styled.div`
  width: 13rem;
  margin-left: 1rem;
`;

const BillingInput = styled.div`
  width: 13rem;
  margin-left: 1rem;
`;

const TierInput = styled.div`
  width: 10rem;
  margin-left: 1rem;
`;

const SortInput = styled.div`
  width: 11rem;
  margin-left: 1rem;
`;

const ResultCount = styled.div`
  position: absolute;
  top: 50%;
  right: ${({ offset }) => offset}rem;
  transform: translateY(-50%);
  color: ${colors.grey40};
  font-size: 0.75rem;
  pointer-events: none;
`;

const SearchResults = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
`;

const SearchResult = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
  border-radius: 0.3125rem;
  box-shadow: 0 0.1875rem 1rem ${colors.grey10};
`;

const Result = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  cursor: pointer;
  user-select: none;
`;

const QuickActions = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: auto;
  width: 5rem;
`;

const QuickImpersonate = styled.div`
  display: flex;
  margin-left: 2rem;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
`;

const QuickKey = styled.div`
  display: flex;
  margin-left: 1rem;
`;

const ResultIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  margin-left: 1rem;
  transform: rotate(-90deg);
  transition: transform 0.1s;

  ${({ isOpen }) =>
    isOpen &&
    css`
      transform: rotate(0deg);
    `}
`;

const ResultIcon = styled(Icon)`
  color: ${colors.grey55};
`;

const NameArea = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  width: 30%;
  min-width: 10rem;
  margin-left: 0.75rem;
`;

const TitleInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.div`
  font-weight: ${weights.black};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const Icons = styled.div`
  display: flex;
  margin-left: 0.75rem;
`;

const NameIcon = styled(Icon)`
  margin-right: 0.5rem;
`;

const StatInfo = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  width: ${({ width }) => width};
  min-width: 5rem;
  margin-left: 0.5rem;
  align-items: ${({ align = 'left' }) => align};
`;

const StatTitle = styled.div`
  font-weight: ${weights.medium};
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
`;

const Subtitle = styled.div`
  color: ${colors.grey55};
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const Logo = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
`;

const WorkspaceImage = styled.img`
  max-height: 100%;
  object-fit: contain;
`;

const BuildingIcon = styled(Icon)`
  font-size: 2rem;
  color: ${colors.grey10};
`;

const DetailNav = styled.ul`
  display: flex;
  padding: 1rem 1.25rem;
  border-top: solid 1px ${colors.grey10};
`;

const DetailNavItem = styled.li`
  font-weight: ${({ isActive }) => (isActive ? weights.bold : weights.normal)};
  cursor: pointer;

  &:not(:last-child) {
    margin-right: 1.5rem;
  }

  &:hover {
    color: ${colors.grey75};
  }
`;

const StatusIndicator = styled.span`
  .icon {
    font-size: 0.625rem;
    color: ${({ color }) => color};

    &:first-child {
      margin-right: 0.7rem;
    }
    &:last-child:not(:first-child) {
      margin-left: 0.5rem;
    }
  }
`;

const Tag = styled.div`
  border-radius: 2rem;
  display: flex;
  padding: 0.25rem 0.5rem;
  align-items: center;
  font-size: 0.75rem;
  font-weight: ${weights.bold};
  white-space: nowrap;
  color: ${({ color = colors.accent }) => color};
  background-color: ${({ background = colors.primary10 }) => background};
`;

function getStatusDisplay(workspace) {
  const billing = workspace.billing;

  if (billing?.isSandbox) {
    return {
      label: 'Sandbox',
      color: colors.grey55,
      tooltip: billing?.associatedWorkspace
        ? `${billing.associatedWorkspace.name} (${billing.associatedWorkspace.key})`
        : undefined,
    };
  }
  if (billing?.associatedWorkspace) {
    return {
      label: 'Associated',
      color: colors.grey55,
      tooltip: `${billing.associatedWorkspace.name} (${billing.associatedWorkspace.key})`,
    };
  }
  if (!billing?.isRequired) {
    return { label: 'Free', color: colors.grey55 };
  }
  // Stripe subscription states
  if (workspace.subscriptionStatus) {
    switch (workspace.subscriptionStatus) {
      case 'trialing': {
        const daysLeft = workspace.stripeTrialEnd ? moment.unix(workspace.stripeTrialEnd).diff(moment(), 'days') : 0;
        return { label: `Stripe Trial (${daysLeft}d)`, color: colors.success50 };
      }
      case 'active':
        if (workspace.cancelAtPeriodEnd) {
          return { label: 'Cancelling', color: colors.warning };
        }
        return { label: 'Subscribed', color: colors.green };
      case 'past_due':
        return { label: 'Past Due', color: colors.danger };
      default:
        return { label: 'Canceled', color: colors.danger, locked: true };
    }
  }
  // Has Stripe customer but no subscription record
  if (billing?.stripeCustomerId) {
    return { label: 'No Subscription', color: colors.danger, locked: true };
  }
  // Internal trial (no Stripe customer)
  if (billing?.trialEndsAt) {
    const daysLeft = moment(billing.trialEndsAt).diff(moment(), 'days');
    if (daysLeft >= 0) {
      return { label: `Internal Trial (${daysLeft}d)`, color: colors.success50 };
    }
    return { label: 'Internal Trial Expired', color: colors.danger, locked: true };
  }
  return { label: 'No Billing', color: colors.danger, locked: true };
}

export default function Search() {
  const location = useLocation();
  const history = useHistory();
  const urlParams = new URLSearchParams(location.search);

  const api = useApi();
  const { impersonate } = useImpersonation();
  const { navigateWorkspace } = useWorkspace();
  const [isSearching, setIsSearching] = useState(false);
  const [searchValue, setSearchValue] = useState(urlParams.get('search') ?? '');
  const [searchText, setSearchText] = useState(urlParams.get('search') ?? '');
  const [searchStatus, setSearchStatus] = useState(
    urlParams.has('status') ? urlParams.get('status') : 'active,restricted',
  );
  const [searchBilling, setSearchBilling] = useState(
    urlParams.has('billing') ? urlParams.get('billing') : 'not_locked',
  );
  const [searchTier, setSearchTier] = useState(urlParams.get('tier') ?? '');
  const [searchSort, setSearchSort] = useState(urlParams.get('sort') ?? 'created');
  const [searchResults, setSearchResults] = useState();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [viewState, setViewState] = useState({});
  const searchRef = useRef();
  const pageRef = useRef(0);

  const search = useCallback(async () => {
    pageRef.current = 0;
    const params = {
      text: searchText,
      statusId: searchStatus,
      filter: searchBilling,
      tier: searchTier,
      sort: searchSort,
      page: 0,
    };
    const cleanedParams = _.omitBy(emptyStringToNull(params), _.isNil);

    setIsSearching(true);
    try {
      const { data } = await api.admin.workspaces.get(cleanedParams);
      setSearchResults(data);
    } finally {
      setIsSearching(false);
    }
  }, [api, searchText, searchStatus, searchBilling, searchTier, searchSort]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !searchResults || searchResults.total <= searchResults.results.length) return;
    pageRef.current += 1;
    const params = {
      text: searchText,
      statusId: searchStatus,
      filter: searchBilling,
      tier: searchTier,
      sort: searchSort,
      page: pageRef.current,
    };
    const cleanedParams = _.omitBy(emptyStringToNull(params), _.isNil);
    setIsLoadingMore(true);
    try {
      const { data } = await api.admin.workspaces.get(cleanedParams);
      setSearchResults((prev) => ({
        ...data,
        results: [...prev.results, ...data.results],
      }));
    } finally {
      setIsLoadingMore(false);
    }
  }, [api, searchText, searchStatus, searchBilling, searchTier, searchSort, searchResults, isLoadingMore]);

  useEffect(() => {
    search();
    return search.cancel;
  }, [search]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchText) params.set('search', searchText);
    params.set('status', searchStatus);
    params.set('billing', searchBilling);
    if (searchTier) params.set('tier', searchTier);
    if (searchSort && searchSort !== 'created') params.set('sort', searchSort);
    const qs = params.toString();
    history.replace({ search: qs ? `?${qs}` : '' });
  }, [searchText, searchStatus, searchBilling, searchTier, searchSort, history]);

  useEffect(() => {
    searchRef.current.focus();
  }, []);

  const emitSearchChange = useMemo(() => _.debounce((value) => setSearchText(value), 250), []);

  useEffect(() => {
    return () => emitSearchChange.cancel();
  }, [emitSearchChange]);

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
    emitSearchChange(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setSearchText('');
  };

  const toggleDetails = (workspaceId) => {
    setViewState((current) => {
      const next = { ...current };
      if (!next[workspaceId]) {
        next[workspaceId] = {
          open: false,
          view: 'general',
        };
      }
      next[workspaceId].open = !next[workspaceId].open;
      return next;
    });
  };

  const changeView = (workspaceId, view) => {
    setViewState((current) => {
      const next = { ...current };
      next[workspaceId].view = view;
      return next;
    });
  };

  return (
    <SearchArea>
      <SearchFilters>
        <SearchInput>
          <input
            ref={searchRef}
            value={searchValue}
            onChange={handleSearchChange}
            type="search"
            placeholder="Find a workspace"
          />
          {!!searchValue && (
            <ClearButton isAnchor={true} onClick={handleClearSearch}>
              <Icon icon="times" />
            </ClearButton>
          )}
          {isSearching ? (
            <SearchIndicator offset={searchValue ? 2.5 : 0.875}>
              <Icon icon="spinner" spin={true} />
            </SearchIndicator>
          ) : (
            searchResults && (
              <ResultCount offset={searchValue ? 2.5 : 0.875}>{searchResults.total.toLocaleString()}</ResultCount>
            )
          )}
        </SearchInput>
        <TierInput>
          <SingleSelect
            placeholder="Tier"
            showEmptyOption={true}
            value={searchTier}
            onChange={(event) => setSearchTier(event.target.value)}>
            <option value="1">Tier 1</option>
            <option value="2">Tier 2</option>
            <option value="3">Tier 3</option>
          </SingleSelect>
        </TierInput>
        <BillingInput>
          <SingleSelect
            placeholder="Billing"
            showEmptyOption={true}
            value={searchBilling}
            onChange={(event) => setSearchBilling(event.target.value)}>
            <option value="not_locked">Not Locked</option>
            <option value="trial">Trial</option>
            <option value="internal_trial">Internal Trial</option>
            <option value="stripe_trial">Stripe Trial</option>
            <option value="subscribed">Subscribed</option>
            <option value="cancelling">Cancelling</option>
            <option value="past_due">Past Due</option>
            <option value="no_subscription">No Subscription</option>
            <option value="sandbox">Sandbox</option>
            <option value="associated">Associated</option>
            <option value="free">Free</option>
            <option value="locked">Locked</option>
          </SingleSelect>
        </BillingInput>
        <StatusInput>
          <SingleSelect
            placeholder="Status"
            showEmptyOption={true}
            value={searchStatus}
            onChange={(event) => setSearchStatus(event.target.value)}>
            <option value="active,restricted">Not Disabled</option>
            <option value="active">Active</option>
            <option value="restricted">Restricted</option>
            <option value="disabled">Disabled</option>
          </SingleSelect>
        </StatusInput>
        <SortInput>
          <SingleSelect placeholder="Sort" value={searchSort} onChange={(event) => setSearchSort(event.target.value)}>
            <option value="created">Newest</option>
            <option value="name">Name (A-Z)</option>
            <option value="members">Most Members</option>
            <option value="ltv">Highest LTV</option>
          </SingleSelect>
        </SortInput>
      </SearchFilters>
      {searchResults && (
        <SearchResults>
          {searchResults.results.map((workspace) => {
            const status = getStatusDisplay(workspace);
            const canQuickImpersonate = !!workspace.defaultImpersonatedMemberId;
            return (
              <SearchResult key={`workspace_${workspace.id}`}>
                <Result onClick={() => toggleDetails(workspace.id)}>
                  <Logo>
                    {workspace.imageUrl ? (
                      <WorkspaceImage src={workspace.imageUrl} alt={workspace.name} />
                    ) : (
                      <BuildingIcon icon="building" />
                    )}
                  </Logo>
                  <NameArea>
                    <TitleInfo>
                      <Title>{workspace.name}</Title>
                      <Subtitle>{workspace.key}</Subtitle>
                    </TitleInfo>
                    <Icons>
                      {workspace.hasSampleData && (
                        <Tooltip message="Sample Data">
                          <NameIcon icon="flask" color={colors.primary50} />
                        </Tooltip>
                      )}
                      {workspace.statusId === 'disabled' && (
                        <Tooltip message="Disabled">
                          <NameIcon icon="shield-exclamation" color={colors.danger} />
                        </Tooltip>
                      )}
                      {workspace.statusId === 'restricted' && (
                        <Tooltip message="Restricted">
                          <NameIcon icon="shield-exclamation" color={colors.warning} />
                        </Tooltip>
                      )}
                    </Icons>
                  </NameArea>
                  <StatInfo width="5rem" align="center">
                    <Tag
                      color={{ 1: colors.success, 2: colors.warning, 3: colors.grey55 }[workspace.tier]}
                      background={{ 1: colors.success10, 2: colors.warning10, 3: colors.grey5 }[workspace.tier]}>
                      {`Tier ${workspace.tier}`}
                    </Tag>
                    <Subtitle style={{ marginTop: '0.25rem', fontSize: '0.625rem' }}>
                      {workspace.seatCount} {workspace.seatCount === 1 ? 'seat' : 'seats'}
                    </Subtitle>
                  </StatInfo>
                  <StatInfo width="7rem" align="center">
                    <Tag
                      color={{ pro: colors.accent, team: colors.grey100 }[workspace.billing?.stripeProductKey]}
                      background={{ pro: colors.primary10, team: colors.grey10 }[workspace.billing?.stripeProductKey]}>
                      {{ pro: 'Pro Plan', team: 'Team Plan' }[workspace.billing?.stripeProductKey]}
                    </Tag>
                    {workspace.billing?.isRequired && workspace.seatPrice != null && (
                      <Subtitle style={{ marginTop: '0.25rem', fontSize: '0.625rem' }}>
                        ${Number(workspace.seatPrice).toFixed(2)}/mo
                      </Subtitle>
                    )}
                  </StatInfo>
                  <StatInfo width="10rem">
                    <StatTitle>
                      {status.tooltip ? (
                        <Tooltip message={status.tooltip}>
                          <StatusIndicator color={status.color}>
                            <Icon icon="circle" />
                            <span>{status.label}</span>
                          </StatusIndicator>
                        </Tooltip>
                      ) : (
                        <StatusIndicator color={status.color}>
                          <Icon icon="circle" />
                          <span>{status.label}</span>
                          {status.locked && <Icon icon="lock" />}
                        </StatusIndicator>
                      )}
                    </StatTitle>
                  </StatInfo>
                  <StatInfo width="7rem" align="center">
                    <StatTitle>{moment(workspace.createdAt).format(dateFormats.compactDate)}</StatTitle>
                    <Subtitle>{`created ${moment(workspace.createdAt).fromNow(true)} ago`}</Subtitle>
                  </StatInfo>
                  <StatInfo width="7rem" align="center">
                    <StatTitle>{workspace.memberCount} </StatTitle>
                    <Subtitle>{workspace.memberCount === 1 ? 'active member' : 'active members'}</Subtitle>
                  </StatInfo>
                  <QuickActions>
                    <QuickImpersonate
                      onClick={async (event) => {
                        event.stopPropagation();
                        if (!canQuickImpersonate) {
                          return;
                        }
                        await impersonate(workspace.defaultImpersonatedMemberId);
                        await navigateWorkspace(workspace.key);
                      }}
                      $disabled={!canQuickImpersonate}>
                      <Tooltip message={canQuickImpersonate ? 'Default Impersonate' : 'No default admin member found'}>
                        <Icon icon="user-secret" color={canQuickImpersonate ? colors.primary : colors.grey25} />
                      </Tooltip>
                    </QuickImpersonate>
                    <QuickKey
                      onClick={(event) => {
                        navigator.clipboard.writeText(workspace.id);
                        event.stopPropagation();
                      }}>
                      <Tooltip message={`Copy Workspace ID to Clipboard`}>
                        <Icon icon="clipboard" color={colors.primary} />
                      </Tooltip>
                    </QuickKey>
                  </QuickActions>
                  <ResultIndicator isOpen={viewState[workspace.id]?.open === true}>
                    <ResultIcon icon="angle-down" />
                  </ResultIndicator>
                </Result>
                {viewState[workspace.id]?.open === true && (
                  <>
                    <DetailNav>
                      <DetailNavItem
                        isActive={viewState[workspace.id]?.view === 'general'}
                        onClick={() => changeView(workspace.id, 'general')}>
                        General
                      </DetailNavItem>
                      <DetailNavItem
                        isActive={viewState[workspace.id]?.view === 'members'}
                        onClick={() => changeView(workspace.id, 'members')}>
                        Members
                      </DetailNavItem>
                      <DetailNavItem
                        isActive={viewState[workspace.id]?.view === 'actions'}
                        onClick={() => changeView(workspace.id, 'actions')}>
                        Actions
                      </DetailNavItem>
                    </DetailNav>
                    {viewState[workspace.id]?.view === 'general' && <General workspaceId={workspace.id} />}
                    {viewState[workspace.id]?.view === 'members' && <Members workspaceId={workspace.id} />}
                    {viewState[workspace.id]?.view === 'actions' && (
                      <Actions workspace={workspace} onChange={() => search()} />
                    )}
                  </>
                )}
              </SearchResult>
            );
          })}
          {searchResults.total > searchResults.results.length && (
            <InfiniteScrollingObserver key={searchResults.results.length} onIntersecting={loadMore} />
          )}
        </SearchResults>
      )}
    </SearchArea>
  );
}
